<template>
  <div
    class="signature-item-maker-cart"
    @click="$emit('item-clicked')"
    @keydown.enter="$emit('item-clicked')"
    @keydown.space.prevent="$emit('item-clicked')"
    role="button"
    tabindex="0"
    :aria-label="`View details for ${props.item.makercart_activity || props.item.title}`"
  >
    <div class="signature-item-content">
      <!-- Header with Badge and Heart Icon -->
      <div class="signature-header">
        <div class="signature-badge-container">
          <Badge
            :variant="
              props.item.series === 'TrendX' ? 'purple' : 'yellow-light'
            "
            :label="props.item.series"
          />
          <div class="signature-theme">
            <span class="theme-text"
              >Theme: {{ props.item.theme || "Sustainability" }}</span
            >
          </div>
        </div>
        <div class="signature-heart-container">
          <svg
            width="22"
            height="20"
            viewBox="0 0 22 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.66275 11.7111L8.82377 18.2042C10.0068 19.4509 11.9932 19.4509 13.1762 18.2042L19.3372 11.7111C21.5542 9.37467 21.5543 5.58651 19.3373 3.25003C17.1203 0.913562 13.5258 0.913563 11.3088 3.25004C11.1409 3.42693 10.8591 3.42693 10.6912 3.25004C8.47421 0.913563 4.87975 0.913563 2.66275 3.25003C0.44575 5.58651 0.445751 9.37467 2.66275 11.7111Z"
              stroke="#E00069"
              stroke-width="1.5"
            />
          </svg>
        </div>
      </div>

      <!-- Image Grid -->
      <div class="signature-image-grid">
        <div class="signature-image-card">
          <img
            :src="props.item.primaryImage || props.item.image"
            alt="Primary activity image"
            class="signature-image"
            @error="handleImageError"
          />
        </div>
        <div class="signature-image-card">
          <img
            :src="props.item.secondaryImage || props.item.image"
            alt="Secondary activity image"
            class="signature-image"
            @error="handleImageError"
          />
        </div>
      </div>

      <!-- Hover Details Section -->
      <div class="signature-details-container">
        <div class="signature-details-content">
          <!-- Title Section -->
          <div class="signature-title-section">
            <h3 class="signature-main-title">
              {{ props.item.makercart_activity || props.item.title }}
            </h3>
            <p class="signature-subtitle">
              {{ props.item.subtitle || "Harness Nature, Power the Future" }}
            </p>
          </div>

          <div class="signature-divider"></div>

          <div class="signature-description">
            <p class="signature-description-text">
              {{
                props.item.description ||
                "This cart provides students with practical, hands-on experiences in environmental science. Through activities focused on renewable energy, sustainable design, and natural resources, students learn the basics of eco-friendly technology and sustainable practices, helping them understand the impact of science on the environment."
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { defineProps, defineEmits } from "vue";
import StemStudioService from "@/services/stemStudioService";
import Badge from "@/components/common/Badge.vue";

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isLogged: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update-liked-status", "item-clicked"]);

const handleLikeClick = async (id) => {
  try {
    const response = await StemStudioService.likeMakercart(id);
    if (response.status === 200) {
      emit("update-liked-status", { id, liked: response.data.data.liked });
    }
  } catch (err) {
    console.error("Error updating like status:", err.message);
  }
};

const handleImageError = (event) => {
  event.target.src = "/images/content-placeholder.svg";
};
</script>

<style scoped>
.signature-item-maker-cart {
  background-color: #f2f3fb;
  border-radius: 16px;
  border: 1px solid #d8dae5;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
  height: 450px;
  display: flex;
  flex-direction: column;
}

.signature-item-maker-cart:hover {
  transform: translateY(-5px);
}

.signature-item-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  height: 100%;
  position: relative;
}

.signature-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.signature-badge-container {
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
}

.signature-theme {
  flex: 1;
  min-width: 0;
}

.theme-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--black-body-text);
}

.signature-heart-container {
  position: relative;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.signature-heart-icon {
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.signature-heart-icon:hover {
  transform: scale(1.1);
}

.signature-image-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  height: 271px;
  width: 100%;
}

.signature-image-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #d8dae5;
}

.signature-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.signature-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.signature-main-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

.signature-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

/* Hover Details Section */
.signature-details-container {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 16px;
  padding-bottom: 40px;
  height: 180px;
  z-index: 3;
  background-color: #f2f3fb;
  border-radius: 0 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transform: translateY(calc(100% - 120px));
  transition:
    transform 0.4s ease,
    padding-bottom 0.4s ease;
}

.signature-item-maker-cart:hover .signature-details-container {
  transform: translateY(0px);
  padding-bottom: 16px;
}

.signature-details-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.signature-divider {
  width: 100%;
  height: 1px;
  border-bottom: 2px dashed #98a2b3;
  margin: 4px 0;
  opacity: 0;
}

.signature-item-maker-cart:hover .signature-divider {
  opacity: 1;
}

.signature-description {
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.2s;
}

.signature-item-maker-cart:hover .signature-description {
  opacity: 1;
  transform: translateY(0);
}

.signature-description-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  color: #3b4648;
  margin: 0;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-right: 12px;
  text-overflow: ellipsis;
}

/* Responsive Design */
@media (max-width: 768px) {
  .signature-item-maker-cart {
    height: 400px;
  }

  .signature-item-content {
    padding: 12px;
    gap: 8px;
  }

  .signature-badge-container {
    gap: 12px;
  }

  .signature-image-grid {
    height: 180px;
    gap: 6px;
  }

  .signature-main-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .signature-subtitle {
    font-size: 18px;
    line-height: 24px;
  }

  .theme-text {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .signature-details-container {
    height: 200px;
    padding: 12px;
    padding-bottom: 30px;
    transform: translateY(calc(100% - 60px)) !important;
  }

  .signature-description-text {
    font-size: 13px;
    line-height: 18px;
  }

  /* Disable all hover transitions on mobile */
  .signature-item-maker-cart:hover {
    transform: none;
  }

  .signature-item-maker-cart:hover .signature-divider {
    opacity: 0;
  }

  .signature-item-maker-cart:hover .signature-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .signature-heart-icon:hover {
    transform: none;
  }
}

@media (max-width: 480px) {
  .signature-item-maker-cart {
    height: 300px;
  }

  .signature-item-content {
    padding: 10px;
  }

  .signature-image-grid {
    height: 170px;
  }

  .signature-main-title {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  .signature-subtitle {
    font-size: var(--fs-medium);
    line-height: 22px;
  }

  .signature-details-container {
    height: 180px;
    padding: 10px;
    padding-bottom: 25px;
    transform: translateY(calc(100% - 80px)) !important;
  }

  .signature-description-text {
    font-size: 12px;
    line-height: 16px;
  }

  /* Disable all hover transitions on smaller mobile */
  .signature-item-maker-cart:hover {
    transform: none;
  }

  .signature-item-maker-cart:hover .signature-divider {
    opacity: 0;
  }

  .signature-item-maker-cart:hover .signature-description {
    opacity: 0;
    transform: translateY(20px);
  }

  .signature-heart-icon:hover {
    transform: none;
  }
}
</style>
